import { useState, useEffect, useRef, useCallback, FC } from "react";
import { PrimaryButton, SecondaryButton } from "./Buttons";
import Webcam from "react-webcam";

interface WebcamCaptureProps {
  photo?: string;
  setPhoto: (photo?: string) => void;
  setCheck?: (check: boolean) => void;
}

const WebcamCapture: FC<WebcamCaptureProps> = ({
  photo,
  setPhoto,
  setCheck,
}) => {
  const webcamRef = useRef<Webcam>(null);
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [permissionGranted, setPermissionGranted] = useState<boolean>(false);

  const isMobile =
    typeof navigator !== "undefined" &&
    /Mobi|Android/i.test(navigator.userAgent);

  const videoConstraintsBase: any = {
  // default, can be overridden below
    facingMode: "environment",
  };

  const handleDevices = useCallback(
    (mediaDevices: MediaDeviceInfo[]) =>
      setDevices(mediaDevices.filter(({ kind }) => kind === "videoinput")),
    [setDevices]
  );

  const capture = useCallback(() => {
    if (webcamRef.current) {
      const videoElement = webcamRef.current.video!;
      const screenshot = webcamRef.current.getScreenshot({
        width: videoElement.videoWidth,
        height: videoElement.videoHeight,
      });

      setPhoto(screenshot ?? "");
      setCheck?.(false);
    }
  }, [webcamRef, setPhoto, setCheck]);

  useEffect(() => {
    if (navigator.mediaDevices) {
      navigator.mediaDevices
        .enumerateDevices()
        .then(handleDevices)
        .catch(() => { });
    }
  }, [handleDevices]);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        const permission = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        if (permission.state === "prompt" || permission.state === "denied") {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
          for (const track of stream.getTracks()) {
            track.stop();
          }
          // After asking for permission, re-enumerate devices to get labels
          if (navigator.mediaDevices) {
            navigator.mediaDevices
              .enumerateDevices()
              .then(handleDevices)
              .catch(() => { });
          }
        }
        setPermissionGranted(permission.state === "granted");
      } catch {
        // Some browsers may throw for permissions.query; fallback to requesting media
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          for (const track of stream.getTracks()) track.stop();
          setPermissionGranted(true);
          if (navigator.mediaDevices) {
            navigator.mediaDevices
              .enumerateDevices()
              .then(handleDevices)
              .catch(() => { console.error("Error enumerating devices after permission granted"); });
          }
        } catch (err) {
          console.error("Error checking camera permission:", err);
          setPermissionGranted(false);
        }
      }
    };
    checkPermission();
  }, [handleDevices]);

  // Build videoConstraints depending on selection
  const videoConstraints = (() => {
    if (!selectedDevice) return videoConstraintsBase;
    // If selection is 'user' or 'environment', use facingMode
    if (selectedDevice === "user" || selectedDevice === "environment") {
      return { facingMode: { exact: selectedDevice } };
    }
    // Otherwise use deviceId exact
    return { deviceId: { exact: selectedDevice } };
  })();

  const cameraOptions = (() => {
    // On mobile devices we want to force the exterior (environment) camera
    // and NOT show options to switch cameras. When not on mobile, expose
    // explicit device list if available.
    if (isMobile) {
      return [];
    }

    if (devices.length > 0) {
      return devices.map((d) => ({
        id: d.deviceId,
        label: d.label || d.deviceId,
      }));
    }

    return [];
  })();

  useEffect(() => {
    if (!selectedDevice) {
      if (isMobile) {
        // Force the rear camera on mobile and do not expose chooser
        setSelectedDevice("environment");
      } else if (cameraOptions.length > 0) {
        setSelectedDevice(cameraOptions[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOptions]);

  return (
    <div className="flex flex-col items-center">
      {permissionGranted ? (
        <>
          {cameraOptions.length > 0 && (
            <>
              <label className="block items-start ml-4 text-sm/6 font-medium text-gray-900">
                Seleccionar cámara
              </label>
              <select
                value={selectedDevice}
                onChange={(e) => setSelectedDevice(e.target.value)}
                className="m-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
              >
                {cameraOptions.map((device, key) => (
                  <option key={key} value={device.id}>
                    {device.label || `Device ${key + 1}`}
                  </option>
                ))}
              </select>
            </>
          )}

          {devices.length === 0 && !photo && cameraOptions.length === 0 ? (
            <div
              className="text-gray-900 border border-gray-300 rounded-xl bg-gray-100"
              style={{
                width: "540px",
                height: "360px",
                border: "1px solid black",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <p className="text-center m-4">
                No se encontraron dispositivos de cámara conectados, por favor,
                conecte el dispositivo o revise la conexión del mismo
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center m-2">
              {photo ? (
                <>
                  <img src={photo} alt="Webcam screenshot" />
                  <SecondaryButton className="mt-2" onClick={() => setPhoto("")}>
                    Volver a tomar foto
                  </SecondaryButton>
                </>
              ) : (
                <>
                  <Webcam
                    audio={false}
                        videoConstraints={videoConstraints}
                    ref={webcamRef}
                    screenshotFormat="image/png"
                  />
                  <PrimaryButton className="mt-2" onClick={capture}>
                    Tomar foto
                  </PrimaryButton>
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            width: "540px",
            height: "360px",
            border: "1px solid black",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <p> Se necesita permitir el acceso a la cámara.</p>
        </div>
      )}
    </div>
  );
};
export default WebcamCapture;
